import React, { useState, useEffect, useCallback } from 'react';
// Import form sections
import ReporterInformation from './sections/ReporterInformation';
import EventBasicDetails from './sections/EventBasicDetails';
import EventDescription from './sections/EventDescription';
import InjuryDamageAssessment from './sections/InjuryDamageAssessment';
import EnvironmentalConditions from './sections/EnvironmentalConditions';
import EquipmentPersonnelInvolvement from './sections/EquipmentPersonnelInvolvement';
import ReviewAndSubmit from './sections/ReviewAndSubmit';
// Import hooks and utilities
import { useFormData } from './hooks/useFormData';
import useAIAssistance from './hooks/useAIAssistance';

// Temporary placeholder for icons - replace with your preferred icon library
const ChevronLeftIcon = ({ className }) => <span className={className}>‹</span>;
const ChevronRightIcon = ({ className }) => <span className={className}>›</span>;
const HomeIcon = ({ className }) => <span className={className}>🏠</span>;

/**
 * MROSafetyReportForm - Main multi-step safety reporting form component
 * 
 * Features:
 * - 7-step progressive form wizard
 * - PWA support with offline capabilities
 * - Auto-save functionality
 * - Real-time validation
 * - Database integration via Supabase
 * - Responsive design for mobile and desktop
 * - AI data collection assistance
 */

// Initial form state matching database schema
const initialFormState = {
  // Primary fields (auto-generated by database)
  id: null,
  report_number: null,
  
  // Reporter Information
  reporter_name: '',
  reporter_position: '',
  reporter_employee_id: '',
  reporter_department_id: null,
  reporter_email: '',
  reporter_phone: '',
  is_confidential: false,
  
  // Event Basic Information
  event_title: '',
  event_date_time: null,
  location_id: null,
  operational_station_id: null,
  location_description: '',
  
  // Event Classification
  event_category_id: null,
  event_type_id: null,
  priority_level_id: null,
  
  // Aircraft Information
  aircraft_registration: '',
  aircraft_type_id: null,
  aircraft_tail_number: '',
  maintenance_check_type: '',
  work_order_number: '',
  vendor_id: null,
  
  // Event Description
  event_description: '',
  immediate_actions_taken: '',
  potential_consequences: '',
  
  // Environmental Conditions
  weather_conditions: '',
  lighting_conditions: '',
  surface_conditions: '',
  
  // Equipment/Tools Involved
  equipment_involved: '',
  tools_involved: '',
  part_numbers_involved: '',
  
  // Personnel Involved
  other_personnel_involved: '',
  contractor_involved: false,
  contractor_company: '',
  
  // Injury/Damage Information
  injuries_occurred: false,
  injury_description: '',
  property_damage: false,
  damage_description: '',
  estimated_damage_cost: null,
  
  // Environmental/Spill Information
  spill_occurred: false,
  spill_type: '',
  spill_amount_liters: null,
  spill_contained: false,
  environmental_impact: '',
  
  // Near Miss Information
  is_near_miss: false,
  near_miss_description: '',
  
  // Regulatory/Compliance
  regulatory_reporting_required: false,
  regulatory_body: '',
  regulatory_report_number: '',
  
  // Status and Workflow
  report_status: 'Draft',
  requires_investigation: false,
  requires_immediate_action: false,
  
  // PWA fields
  offline_created: false,
  sync_status: 'draft'
};

const MROSafetyReportForm = ({ 
  initialData = {},
  onSubmitSuccess,
  onCancel,
  mode = 'create' // 'create' or 'edit'
}) => {
  // Form state management
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({ ...initialFormState, ...initialData });
  const [errors, setErrors] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Load form lookup data
  const {
    departments,
    locations,
    eventCategories,
    eventTypes,
    aircraftTypes,
    vendors,
    stations,
    priorityLevels,
    loading: dataLoading,
    error: dataError
  } = useFormData();

  // Initialize AI assistance
  const { generateEventTitle } = useAIAssistance(formData, {
    eventCategories,
    eventTypes,
    locations,
    departments,
    vendors,
    stations
  });

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-save functionality
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      if (formData.event_title || formData.event_description) {
        localStorage.setItem('sms-draft-report', JSON.stringify({
          ...formData,
          lastSaved: new Date().toISOString()
        }));
      }
    }, 30000); // Auto-save every 30 seconds

    return () => clearInterval(autoSaveInterval);
  }, [formData]);

  // Load draft from localStorage on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem('sms-draft-report');
    if (savedDraft && mode === 'create') {
      try {
        const draftData = JSON.parse(savedDraft);
        if (window.confirm('Found a saved draft. Would you like to continue from where you left off?')) {
          setFormData(prev => ({ ...prev, ...draftData }));
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [mode]);

  // Form data update handler
  const updateFormData = useCallback((updates) => {
    setFormData(prev => {
      const newData = { ...prev, ...updates };
      
      // Clear related errors when fields are updated
      const updatedErrors = { ...errors };
      Object.keys(updates).forEach(key => {
        if (updatedErrors[key]) {
          delete updatedErrors[key];
        }
      });
      setErrors(updatedErrors);
      
      return newData;
    });
  }, [errors]);

  // Generate AI title when description is updated
  useEffect(() => {
    if (formData.event_description && formData.event_description.length > 50 && !formData.event_title) {
      // Get additional context for title generation
      const locationName = locations?.find(loc => loc.id === formData.location_id)?.location_name || '';
      const categoryName = eventCategories?.find(cat => cat.id === formData.event_category_id)?.category_name || '';
      
      const eventContext = {
        aircraft_registration: formData.aircraft_registration,
        location_name: locationName,
        location_description: formData.location_description,
        event_category_name: categoryName
      };

      const generatedTitle = generateEventTitle(formData.event_description, eventContext);
      
      if (generatedTitle && generatedTitle !== 'Safety Event Report') {
        // Only update if we have a meaningful title
        setFormData(prev => ({
          ...prev,
          event_title: generatedTitle
        }));
      }
    }
  }, [formData.event_description, formData.location_id, formData.event_category_id, formData.aircraft_registration, formData.location_description, locations, eventCategories, generateEventTitle, formData.event_title]);

  // Navigation handlers
  const goToStep = (step) => {
    if (step >= 1 && step <= 7) {
      setCurrentStep(step);
    }
  };

  const goNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, 7));
    }
  };

  const goPrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Validation logic
  const validateCurrentStep = () => {
    const stepErrors = {};
    
    switch (currentStep) {
      case 1: // Reporter Information
        if (!formData.reporter_name?.trim()) {
          stepErrors.reporter_name = 'Reporter name is required';
        }
        if (!formData.reporter_department_id) {
          stepErrors.reporter_department_id = 'Department is required';
        }
        break;
        
      case 2: // Event Basic Details - Title is now AI-generated, so not required here
        if (!formData.event_date_time) {
          stepErrors.event_date_time = 'Event date and time is required';
        }
        if (!formData.location_id) {
          stepErrors.location_id = 'Location is required';
        }
        if (!formData.event_category_id) {
          stepErrors.event_category_id = 'Event category is required';
        }
        break;
        
      case 3: // Event Description
        if (!formData.event_description?.trim()) {
          stepErrors.event_description = 'Event description is required';
        }
        break;
        
      case 4: // Injury/Damage Assessment
        if (formData.contractor_involved && !formData.contractor_company?.trim()) {
          stepErrors.contractor_company = 'Contractor company is required when contractor is involved';
        }
        break;
        
      case 6: // Equipment/Personnel Involvement
        if (formData.contractor_involved && !formData.contractor_company?.trim()) {
          stepErrors.contractor_company = 'Contractor company is required when contractor is involved';
        }
        break;
        
      default:
        // Steps 5 and 7 have no specific validation requirements
        break;
    }
    
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async (finalFormData) => {
    setSubmissionStatus('submitting');
    
    try {
      // Add submission metadata
      const submissionData = {
        ...finalFormData,
        report_status: 'Submitted',
        offline_created: !isOnline,
        sync_status: isOnline ? 'synced' : 'pending',
        submitted_at: new Date().toISOString()
      };

      if (isOnline) {
        // Online submission logic would go here
        // const result = await submitToSupabase(submissionData);
        console.log('Submitting online:', submissionData);
        
        // Simulate submission
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Clear draft
        localStorage.removeItem('sms-draft-report');
        
        setSubmissionStatus('success');
        
        if (onSubmitSuccess) {
          onSubmitSuccess({
            id: 'temp-id',
            report_number: 'REP-2025-001',
            ...submissionData
          });
        }
      } else {
        // Offline submission - store for later sync
        const offlineReports = JSON.parse(localStorage.getItem('sms-offline-reports') || '[]');
        offlineReports.push({
          id: Date.now().toString(),
          ...submissionData,
          created_at: new Date().toISOString()
        });
        localStorage.setItem('sms-offline-reports', JSON.stringify(offlineReports));
        
        setSubmissionStatus('success');
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmissionStatus('error');
    }
  };

  // Progress calculation
  const progress = (currentStep / 7) * 100;

  // Step configuration
  const steps = [
    { number: 1, title: 'Reporter Information', component: ReporterInformation },
    { number: 2, title: 'Event Details', component: EventBasicDetails },
    { number: 3, title: 'Description', component: EventDescription },
    { number: 4, title: 'Safety Impact', component: InjuryDamageAssessment },
    { number: 5, title: 'Environment', component: EnvironmentalConditions },
    { number: 6, title: 'Equipment & Personnel', component: EquipmentPersonnelInvolvement },
    { number: 7, title: 'Review & Submit', component: ReviewAndSubmit }
  ];

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading form data...</p>
        </div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-red-800">Error Loading Form</h3>
            <p className="text-red-600 mt-2">Unable to load form data. Please refresh and try again.</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentStepData = steps[currentStep - 1];
  const CurrentStepComponent = currentStepData.component;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <HomeIcon className="h-6 w-6 text-gray-400" />
              <nav className="flex items-center space-x-2 text-sm text-gray-500">
                <span>SMS Prime</span>
                <ChevronRightIcon className="h-4 w-4" />
                <span className="text-gray-900 font-medium">Safety Reporting</span>
              </nav>
            </div>
            
            <div className="flex items-center space-x-4">
              {!isOnline && (
                <div className="flex items-center space-x-2 text-yellow-600">
                  <div className="h-2 w-2 bg-yellow-600 rounded-full"></div>
                  <span className="text-sm">Offline</span>
                </div>
              )}
              
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {mode === 'create' ? 'New Safety Report' : 'Edit Safety Report'}
              </h1>
              <span className="text-sm text-gray-600">
                Step {currentStep} of 7
              </span>
            </div>
            
            {/* Step indicators */}
            <div className="flex items-center space-x-4 mb-4">
              {steps.map((step) => (
                <button
                  key={step.number}
                  onClick={() => goToStep(step.number)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    step.number === currentStep
                      ? 'bg-blue-100 text-blue-700'
                      : step.number < currentStep
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                    step.number === currentStep
                      ? 'bg-blue-600 text-white'
                      : step.number < currentStep
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-400 text-white'
                  }`}>
                    {step.number < currentStep ? '✓' : step.number}
                  </span>
                  <span className="hidden sm:block">{step.title}</span>
                </button>
              ))}
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <CurrentStepComponent
          formData={formData}
          updateFormData={updateFormData}
          errors={errors}
          loading={submissionStatus === 'submitting'}
          
          // Pass lookup data to sections that need it
          departments={departments}
          locations={locations}
          eventCategories={eventCategories}
          eventTypes={eventTypes}
          aircraftTypes={aircraftTypes}
          vendors={vendors}
          stations={stations}
          priorityLevels={priorityLevels}
          
          // ReviewAndSubmit specific props
          onSubmit={currentStep === 7 ? handleSubmit : undefined}
          onEditSection={currentStep === 7 ? goToStep : undefined}
          isOnline={isOnline}
          submissionStatus={submissionStatus}
        />

        {/* Navigation */}
        {currentStep < 7 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={goPrevious}
              disabled={currentStep === 1}
              className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Previous
            </button>
            
            <button
              onClick={goNext}
              className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Next
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MROSafetyReportForm;
